import { parse, stringify } from 'qs'
import { useCallback, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

const DEFAULT_LIMIT = 50
const MAX_KEYWORD_LENGTH = 20
const MAX_KEYWORD_COUNT = 10

export const validateKeywords = (keywords: string[]): string | undefined => {
  if (keywords.some(k => k.length > MAX_KEYWORD_LENGTH)) {
    return `キーワードは${MAX_KEYWORD_LENGTH}文字までの文字列で指定してください`
  }
  if (keywords.length > MAX_KEYWORD_COUNT) {
    return `キーワードは${MAX_KEYWORD_COUNT}個までで指定してください`
  }

  return
}

type QueryParams = {
  keywords: string[]
  limit: number
  offset: number
}

// TODO: クエリのparse処理がかなり厳しいので、なんらかライブラリを導入するか共通化する必要がある。

const parseKeywords = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.filter((k): k is string => typeof k === 'string')
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return raw.split(',')
  }
  return []
}

const parseIntWithFallback = (raw: unknown, fallback: number): number => {
  if (typeof raw !== 'string') return fallback
  const num = Number(raw)
  return Number.isFinite(num) ? num : fallback
}

const parseQuery = (search: string): QueryParams => {
  const parsed = parse(search, { ignoreQueryPrefix: true })
  return {
    keywords: parseKeywords(parsed['keywords']),
    limit: parseIntWithFallback(parsed['limit'], DEFAULT_LIMIT),
    offset: parseIntWithFallback(parsed['offset'], 0),
  }
}

export const useTableFilters = () => {
  const location = useLocation()
  const history = useHistory()

  const query = useMemo(() => parseQuery(location.search), [location.search])

  const [draftKeyword, setDraftKeyword] = useState(query.keywords.join(' '))

  // フォーム送信時のバリデーションエラー（URLを更新せずに表示するケース）
  const [submitError, setSubmitError] = useState<string | null>(null)

  // URLを直接変更したときのバリデーションエラー（query.keywords から派生）
  const urlError = useMemo(
    () => validateKeywords(query.keywords),
    [query.keywords],
  )

  // フォーム送信エラーを優先し、なければURLエラーを使う
  const keywordError = submitError ?? urlError

  const rowsPerPage = query.limit
  const page = Math.floor(query.offset / query.limit)

  const updateQuery = useCallback(
    (patch: Partial<{ keywords: string[]; limit: number; page: number }>) => {
      const next = {
        keywords: query.keywords,
        limit: query.limit,
        page,
        ...patch,
      }
      const offset = next.page * next.limit
      const search = stringify(
        {
          keywords: next.keywords.length > 0 ? next.keywords : undefined,
          limit: next.limit !== DEFAULT_LIMIT ? next.limit : undefined,
          offset: offset !== 0 ? offset : undefined,
        },
        { arrayFormat: 'comma', skipNulls: true },
      )
      history.replace({ search })
    },
    [query, page, history],
  )

  const handleChangePage = useCallback(
    (newPage: number) => updateQuery({ page: newPage }),
    [updateQuery],
  )

  const handleChangeRowsPerPage = useCallback(
    (limit: number) => {
      if (!Number.isFinite(limit) || limit <= 0) return
      updateQuery({ limit, page: 0 })
    },
    [updateQuery],
  )

  const handleConfirmKeyword = useCallback(() => {
    const keywords = normalizeKeyword(draftKeyword)
      .split(' ')
      .filter(k => k)

    const error = validateKeywords(keywords)
    if (error) {
      setSubmitError(error)
      return
    }

    setSubmitError(null)
    updateQuery({ keywords, page: 0 })
  }, [draftKeyword, updateQuery])

  const handleChangeKeyword = useCallback((keyword: string) => {
    setDraftKeyword(keyword)
    setSubmitError(null)
  }, [])

  return {
    query,
    keyword: draftKeyword,
    keywordError,
    setKeyword: handleChangeKeyword,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleConfirmKeyword,
  }
}

/**
 * ユーザー入力のkeywordを正規化する
 * - 全角スペースを半角スペースに変換する
 * - 二つ以上連続するスペースを一つにまとめる
 * - 先頭と末尾のスペースを削除する
 */
export const normalizeKeyword = (keyword: string): string => {
  return keyword
    .replace(/\u3000/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
